/**
 * ConfiguratorPage - Main page for Prestige 3D Configurator
 * Complete one-to-one port of Vue app functionality
 */
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PergolaDesign,
  type PlacedAccessory,
  type PrestigeConfiguration,
  type PrestigeConfigurationData,
} from "@core";
import {
  mapLocalStateToPrestigeConfigurationData,
  mapPrestigeConfigurationDataToLocalState,
} from "@core";
import {
  FRAME_TEXTURES,
  BLIND_TEXTURES,
  type TextureOption,
} from "@domain/constants/ralColors";
import {
  formatDimensionCm,
  getPrestigeExternalDepthCm,
  getPrestigeExternalHeightCm,
  getPrestigeExternalWidthCm,
  type UnitType,
} from "@domain/services/prestigeDimensionDisplay";
import { calculatePrestigePricing } from "@domain/services/prestigePricing";
import { usePricingConfiguration } from "@hooks";
import { PART_TYPE_NAMES } from "@infrastructure/three/prestigeService";
import type { FaceIDType } from "@ui/hooks/usePrestige";
import { usePrestige } from "@ui/hooks/usePrestige";
import { DimensionOverlay } from "@ui/components/DimensionOverlay";
import { InfoTooltip } from "@ui/components/InfoTooltip";
import { useIsMobile } from "@ui/hooks/useMediaQuery";
import { formatCurrency } from "../../utils/format-currency";
import { stableHash } from "../../utils/stable-hash";
import { functionsService } from "../../services/functions-service";
import {
  ConfiguratorTabContent,
  type ConfigTab,
} from "./configurator/ConfiguratorTabContent";
import { ConfiguratorAccessoryModals } from "./configurator/ConfiguratorAccessoryModals";
import { LoadingOverlay } from "./configurator/LoadingOverlay";
import {
  ShareModal,
  type ShareCopyStatus,
} from "./configurator/ShareModal";
import { ExitConfirmModal } from "./configurator/ExitConfirmModal";
import {
  SummaryModal,
  type AccessorySummaryItem,
  type DesktopSummaryMetrics,
  type SummaryPricing,
} from "./configurator/SummaryModal";
import styles from "./ConfiguratorPage.module.css";
const logoImg = "/images/paragon-logo.svg";
const STORE_URL = "https://paragon-outdoor.com/";
// Shopify applies this as an automatic discount at checkout; here it is purely
// for display (show the full price struck through alongside the discounted one).
const DISCOUNT_RATE = 0.1;
const shareFloatingButtonIcon = "/icons/shareFloatingButton.svg";
const needHelpIcon = "/icons/needHelp.svg";

const TAB_ITEMS: ReadonlyArray<{ key: ConfigTab; label: string }> = [
  { key: "style", label: "Style" },
  { key: "dimensions", label: "Dimensions" },
  { key: "accessories", label: "Sidewalls" },
  { key: "lighting", label: "Accessories" },
  // { key: "scene", label: "Scene" },
  // { key: "summary", label: "Summary" },
];

const MoonIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#71903e"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

const LAST_TAB = TAB_ITEMS.at(-1)!.key;
const CONTACT_TEXT = "Have Questions? Contact our design experts at 877-782-4482";
const HELP_PHONE = "877-782-4482";
const HELP_TEL = "+18777824482";

type ConfiguratorPageProps = {
  configId?: string;
  ownerID?: string | null;
  remoteConfiguration?: PrestigeConfiguration | null;
  saveConfiguration?: (configuration: PrestigeConfigurationData) => Promise<unknown>;
};

type CollaborativePayload = Pick<
  PrestigeConfigurationData,
  "ownerID" | "collaborative" | "design" | "dimensions" | "accessories" | "buildNumber"
>;

const CM_PER_INCH = 2.54;
const INTERNAL_BEAM_DEDUCTION_CM = 9 * CM_PER_INCH;
// Freestanding pergolas lose a post (6") on both ends of an axis.
const INTERNAL_POST_DEDUCTION_CM = 12 * CM_PER_INCH;
// Attached (wall-mounted) pergolas mount one side to the wall, so the depth
// only loses a single post (6") instead of two.
const INTERNAL_SINGLE_POST_DEDUCTION_CM = 6 * CM_PER_INCH;

const getPrestigeInternalWidthCm = ({
  width,
}: {
  width: number;
}): number =>
  Math.round((getPrestigeExternalWidthCm(width) - INTERNAL_POST_DEDUCTION_CM) * 10) / 10;

const getPrestigeInternalDepthCm = ({
  depth,
  attached,
}: {
  depth: number;
  attached: boolean;
}): number => {
  const deduction = attached
    ? INTERNAL_SINGLE_POST_DEDUCTION_CM
    : INTERNAL_POST_DEDUCTION_CM;
  return Math.round((getPrestigeExternalDepthCm(depth) - deduction) * 10) / 10;
};

const getPrestigeInternalHeightCm = ({
  height,
}: {
  height: number;
}): number =>
  Math.round((getPrestigeExternalHeightCm(height) - INTERNAL_BEAM_DEDUCTION_CM) * 10) / 10;

const formatDimensionAxis = ({
  height,
  depth,
  width,
  unit,
}: {
  height: number;
  depth: number;
  width: number;
  unit: UnitType;
}): string =>
  `${formatDimensionCm(height, unit)} H x ${formatDimensionCm(depth, unit)} D x ${formatDimensionCm(width, unit)} W`;

const getTextureInfo = (
  fileName: string,
  textures: TextureOption[],
): { name: string; hex: string } => {
  const match = textures.find((texture) => texture.img === fileName);
  return {
    name: match?.name ?? fileName.replace(".png", "").toUpperCase(),
    hex: match?.hex ?? "#888888",
  };
};

const buildDesktopSummaryMetrics = ({
  widthValue,
  depthValue,
  heightValue,
  roomCount,
  unit,
  frameTexture,
  blindTexture,
  hasWall,
}: {
  widthValue: number;
  depthValue: number;
  heightValue: number;
  roomCount: number;
  unit: UnitType;
  frameTexture: string;
  blindTexture: string;
  hasWall: boolean;
}): DesktopSummaryMetrics => {
  const structure = getTextureInfo(frameTexture, FRAME_TEXTURES);
  const louvre = getTextureInfo(blindTexture, BLIND_TEXTURES);
  const externalDimensions = formatDimensionAxis({
    height: getPrestigeExternalHeightCm(heightValue),
    depth: getPrestigeExternalDepthCm(depthValue),
    width: getPrestigeExternalWidthCm(widthValue),
    unit,
  });
  const internalDimensions = formatDimensionAxis({
    height: getPrestigeInternalHeightCm({ height: heightValue }),
    depth: getPrestigeInternalDepthCm({ depth: depthValue, attached: hasWall }),
    width: getPrestigeInternalWidthCm({ width: widthValue }),
    unit,
  });

  return {
    structureColorName: structure.name,
    structureColorHex: structure.hex,
    louvreColorName: louvre.name,
    louvreColorHex: louvre.hex,
    externalDimensions,
    internalDimensions,
    roomCount,
    louverCount: widthValue,
  };
};

export const ConfiguratorPage = ({
  configId,
  ownerID,
  remoteConfiguration = null,
  saveConfiguration,
}: ConfiguratorPageProps) => {
  const { containerRef, state, actions, service, view } = usePrestige();
  const pricingConfigurationQuery = usePricingConfiguration();
  console.log(pricingConfigurationQuery.error);
  const isMobile = useIsMobile();
  const [frameTexture, setFrameTexture] = useState("7015.png");
  const [blindTexture, setBlindTexture] = useState("7015.png");
  const [scenePreview, setScenePreview] = useState(false);
  const [activeTab, setActiveTab] = useState<ConfigTab>("style");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<ShareCopyStatus>("idle");
  const [showDimensions, setShowDimensions] = useState(true);
  const [collaborativeEnabled, setCollaborativeEnabled] = useState(
    remoteConfiguration?.collaborative ?? true,
  );
  const isHydratingRemoteRef = useRef(false);
  const hasInitialRemoteHydrationRef = useRef(false);
  const saveTimeoutRef = useRef<number | null>(null);
  const scheduledLocalHashRef = useRef<string | null>(null);
  const pendingLocalHashesRef = useRef<Map<string, number>>(new Map());
  const lastAppliedRemoteHashRef = useRef<string | null>(null);
  const serializedPlacedAccessoriesRef = useRef<PlacedAccessory[]>([]);
  const collaborativeOwnerID = remoteConfiguration?.ownerID || ownerID || "";
  const isCollaborativeMode =
    Boolean(configId) &&
    Boolean(collaborativeOwnerID) &&
    Boolean(saveConfiguration);
  const isOwner = Boolean(
    ownerID &&
    remoteConfiguration?.ownerID &&
    ownerID === remoteConfiguration.ownerID,
  );
  const canToggleCollaborative = isCollaborativeMode && isOwner;
  const isViewerReadOnly =
    isCollaborativeMode && !isOwner && !collaborativeEnabled;

  const incrementPendingLocalHash = useCallback((hash: string) => {
    const previous = pendingLocalHashesRef.current.get(hash) ?? 0;
    pendingLocalHashesRef.current.set(hash, previous + 1);
  }, []);

  const decrementPendingLocalHash = useCallback((hash: string) => {
    const previous = pendingLocalHashesRef.current.get(hash) ?? 0;
    if (previous <= 1) {
      pendingLocalHashesRef.current.delete(hash);
      return;
    }

    pendingLocalHashesRef.current.set(hash, previous - 1);
  }, []);

  useEffect(() => {
    pendingLocalHashesRef.current.clear();
    lastAppliedRemoteHashRef.current = null;
    scheduledLocalHashRef.current = null;
    isHydratingRemoteRef.current = false;
    hasInitialRemoteHydrationRef.current = false;
    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, [configId]);

  useEffect(() => {
    setCollaborativeEnabled(remoteConfiguration?.collaborative ?? true);
  }, [configId, remoteConfiguration?.collaborative]);

  useEffect(() => {
    if (isShareModalOpen || isExitModalOpen) {
      return;
    }
    setCopyStatus("idle");
  }, [isShareModalOpen, isExitModalOpen]);
  const desktopSummaryMetrics = useMemo(
    () =>
      buildDesktopSummaryMetrics({
        widthValue: state.widthValue,
        depthValue: state.depthValue,
        heightValue: state.heightValue,
        roomCount: state.roomCount,
        unit: state.unit,
        frameTexture,
        blindTexture,
        hasWall: state.hasWall,
      }),
    [
      blindTexture,
      frameTexture,
      state.depthValue,
      state.heightValue,
      state.roomCount,
      state.unit,
      state.widthValue,
      state.hasWall,
    ],
  );

  const {
    addPart,
    applyBlindColor,
    applyFrameColor,
    canAddPart,
    clearClickedFace,
    closeCustomizationModal,
    deleteAllParts,
    deletePart,
    getAccessoryLeftRight,
    getAccessoryMovement,
    getAccessorySecondaryMovement,
    getAccessoryTexture,
    getAccessoryTextureSecondary,
    getMergeDoorCount,
    getMergeDoors,
    openCustomization,
    setDepth,
    setHeight,
    setBlindRotation,
    setScene,
    setSceneVisibility,
    setUnit,
    setAccessoryLeftRight,
    setAccessoryMovement,
    setAccessoryMovementSecondary,
    setAccessoryTexture,
    setAccessoryTextureSecondary,
    setWallMounted,
    setWidth,
    startAddMode,
    stopAddMode,
    toggleLedVisibility,
    setHeaterQuantity,
    toggleMergeDoor,
    toggleNightMode,
  } = actions;

  const applyFrameTexture = useCallback(
    (textureImg: string) => {
      setFrameTexture(textureImg);
      applyFrameColor(textureImg);
    },
    [applyFrameColor],
  );

  const applyBlindTexture = useCallback(
    (textureImg: string) => {
      setBlindTexture(textureImg);
      applyBlindColor(textureImg);
    },
    [applyBlindColor],
  );

  const serializePlacedAccessories = useCallback((): PlacedAccessory[] => {
    if (!service) {
      return [];
    }

    return service
      .getSurfacePartList()
      .filter(
        (surfacePart) =>
          surfacePart.visible &&
          surfacePart.partType !== null &&
          surfacePart.partType !== 0 &&
          surfacePart.part !== null,
      )
      .map((surfacePart) => {
        const faceId = surfacePart.faceId as PlacedAccessory["faceId"];
        const partType = surfacePart.partType as number;
        const capabilities = service.getAccessoryCapabilities(partType);
        const placedAccessory: PlacedAccessory = {
          faceId,
          partType,
        };

        if (capabilities.canMove) {
          placedAccessory.movement = service.getAccessoryMovement(
            surfacePart.faceId,
          );
        }

        if (capabilities.canMoveSecondary) {
          placedAccessory.secondaryMovement =
            service.getAccessorySecondaryMovement(surfacePart.faceId);
        }

        if (capabilities.hasLeftRight) {
          placedAccessory.isLeft = service.getAccessoryLeftRight(
            surfacePart.faceId,
          );
        }

        const texture = service.getAccessoryTexture(surfacePart.faceId);
        if (texture) {
          placedAccessory.texture = texture;
        }

        const texture2 = service.getAccessoryTextureSecondary(surfacePart.faceId);
        if (texture2) {
          placedAccessory.texture2 = texture2;
        }

        if (capabilities.isMergeType) {
          const mergeDoors = service.getMergeDoors(surfacePart.faceId);
          if (mergeDoors.length > 0) {
            placedAccessory.mergeDoors = mergeDoors;
          }
        }

        return placedAccessory;
      })
      .sort((first, second) => first.faceId.localeCompare(second.faceId));
  }, [service]);

  const applyPlacedAccessories = useCallback(
    async (placedAccessories: PlacedAccessory[]) => {
      if (!service) {
        return;
      }

      deleteAllParts();

      const sortedAccessories = [...placedAccessories].sort((first, second) =>
        first.faceId.localeCompare(second.faceId),
      );

      for (const accessory of sortedAccessories) {
        const faceId = accessory.faceId as FaceIDType;

        if (!canAddPart(faceId)) {
          continue;
        }

        await addPart(faceId, accessory.partType, { openCustomization: false });

        if (typeof accessory.isLeft === "boolean") {
          service.setAccessoryLeftRight(faceId, accessory.isLeft);
        }

        if (typeof accessory.movement === "number") {
          service.moveAccessory(faceId, accessory.movement);
        }

        if (typeof accessory.secondaryMovement === "number") {
          service.moveAccessorySecondary(faceId, accessory.secondaryMovement);
        }

        if (accessory.texture) {
          service.setAccessoryTexture(faceId, accessory.texture);
        }

        if (accessory.texture2) {
          service.setAccessoryTextureSecondary(faceId, accessory.texture2);
        }

        if (accessory.mergeDoors?.length) {
          const mergeDoorCount = service.getMergeDoorCount(faceId);
          const count = Math.min(mergeDoorCount, accessory.mergeDoors.length);
          for (let index = 0; index < count; index += 1) {
            service.updateMergeDoorsStatus(
              faceId,
              index,
              Boolean(accessory.mergeDoors[index]),
            );
          }
        }
      }

    },
    [addPart, canAddPart, deleteAllParts, service],
  );

  // Get surface part list from service
  const surfacePartList = service?.getSurfacePartList() ?? [];
  const accessorySummary: AccessorySummaryItem[] = (() => {
    const counts = new Map<number, number>();
    surfacePartList.forEach((part) => {
      if (!part.visible || part.partType === null || part.partType === 0) {
        return;
      }
      counts.set(part.partType, (counts.get(part.partType) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([partType, count]) => ({
        partType,
        name: PART_TYPE_NAMES[partType] ?? `Accessory ${partType}`,
        count,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();
  const isLastTab = activeTab === LAST_TAB;
  const desktopNextButtonLabel = isLastTab ? "View Summary" : "Next";

  const handleShareModalOpen = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleLogoClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setIsExitModalOpen(true);
    },
    [],
  );

  const handleExitStay = useCallback(() => {
    setIsExitModalOpen(false);
  }, []);

  const handleExitDiscard = useCallback(() => {
    setIsExitModalOpen(false);
    window.location.href = STORE_URL;
  }, []);

  const handleCopyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus("success");
    } catch (error) {
      console.error("Failed to copy share link", error);
      setCopyStatus("error");
    }
  }, []);

  const handleCollaborativeToggle = useCallback(() => {
    if (!canToggleCollaborative) {
      return;
    }
    setCollaborativeEnabled((previous) => !previous);
  }, [canToggleCollaborative]);

  const handleWallChange = useCallback((value: boolean) => {
    if (isViewerReadOnly) {
      return;
    }
    setWallMounted(value);
  }, [isViewerReadOnly, setWallMounted]);

  const handleFrameTextureSelect = useCallback((texture: TextureOption) => {
    if (isViewerReadOnly) {
      return;
    }
    applyFrameTexture(texture.img);
  }, [applyFrameTexture, isViewerReadOnly]);

  const handleBlindTextureSelect = useCallback((texture: TextureOption) => {
    if (isViewerReadOnly) {
      return;
    }
    applyBlindTexture(texture.img);
  }, [applyBlindTexture, isViewerReadOnly]);

  const handleBlindRotationChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setBlindRotation(value);
  }, [isViewerReadOnly, setBlindRotation]);

  const handleWidthChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setWidth(value);
  }, [isViewerReadOnly, setWidth]);

  const handleDepthChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setDepth(value);
  }, [isViewerReadOnly, setDepth]);

  const handleHeightChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setHeight(value);
  }, [isViewerReadOnly, setHeight]);

  const handleLedToggle = useCallback(() => {
    if (isViewerReadOnly) {
      return;
    }
    toggleLedVisibility();
  }, [isViewerReadOnly, toggleLedVisibility]);

  const handleHeaterQtyChange = useCallback((qty: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setHeaterQuantity(qty);
  }, [isViewerReadOnly, setHeaterQuantity]);

  const handleNightModeToggle = useCallback(() => {
    if (isViewerReadOnly) {
      return;
    }
    toggleNightMode();
  }, [isViewerReadOnly, toggleNightMode]);

  // Accessory handlers
  const handleRemovePart = useCallback((faceId: string) => {
    if (isViewerReadOnly) {
      return;
    }
    deletePart(faceId as Parameters<typeof deletePart>[0]);
  }, [deletePart, isViewerReadOnly]);

  const handleCustomizePart = useCallback((faceId: string, partType: number) => {
    if (isViewerReadOnly) {
      return;
    }
    openCustomization(faceId as FaceIDType, partType);
  }, [isViewerReadOnly, openCustomization]);

  const handleSelectAccessory = useCallback((partType: number) => {
    if (isViewerReadOnly) {
      return;
    }
    if (state.activeAccessoryType === partType && state.isAddMode) {
      stopAddMode();
      return;
    }
    startAddMode(partType);
  }, [
    isViewerReadOnly,
    startAddMode,
    state.activeAccessoryType,
    state.isAddMode,
    stopAddMode,
  ]);

  const handleExitAddMode = useCallback(() => {
    stopAddMode();
  }, [stopAddMode]);

  const handleResetAccessories = useCallback(() => {
    if (isViewerReadOnly) {
      return;
    }
    deleteAllParts();
  }, [deleteAllParts, isViewerReadOnly]);

  // Face click modal handlers
  const handleModalAddAccessory = useCallback(async (partType: number) => {
    if (isViewerReadOnly) {
      return;
    }
    if (state.clickedFaceId) {
      await addPart(state.clickedFaceId, partType);
    }
  }, [addPart, isViewerReadOnly, state.clickedFaceId]);

  const handleModalRemoveAccessory = useCallback(() => {
    if (isViewerReadOnly) {
      return;
    }
    if (state.clickedFaceId) {
      deletePart(state.clickedFaceId);
    }
  }, [deletePart, isViewerReadOnly, state.clickedFaceId]);

  const handleAccessoryMovementChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setAccessoryMovement(value);
  }, [isViewerReadOnly, setAccessoryMovement]);

  const handleAccessorySecondaryMovementChange = useCallback((value: number) => {
    if (isViewerReadOnly) {
      return;
    }
    setAccessoryMovementSecondary(value);
  }, [isViewerReadOnly, setAccessoryMovementSecondary]);

  const handleAccessoryLeftRightChange = useCallback((value: boolean) => {
    if (isViewerReadOnly) {
      return;
    }
    setAccessoryLeftRight(value);
  }, [isViewerReadOnly, setAccessoryLeftRight]);

  const handleAccessoryMergeDoorToggle = useCallback((doorIndex: number, visible: boolean) => {
    if (isViewerReadOnly) {
      return;
    }
    toggleMergeDoor(doorIndex, visible);
  }, [isViewerReadOnly, toggleMergeDoor]);

  const handleAccessoryTextureChange = useCallback((textureImg: string) => {
    if (isViewerReadOnly) {
      return;
    }
    setAccessoryTexture(textureImg);
  }, [isViewerReadOnly, setAccessoryTexture]);

  const handleAccessoryTextureSecondaryChange = useCallback((textureImg: string) => {
    if (isViewerReadOnly) {
      return;
    }
    setAccessoryTextureSecondary(textureImg);
  }, [isViewerReadOnly, setAccessoryTextureSecondary]);

  const handleSceneChange = (
    sceneId: Parameters<typeof setScene>[0],
  ) => {
    if (!isMobile) {
      setScenePreview(true);
    }
    void setScene(sceneId);
  };

  const moveToNextTab = () => {
    const currentIndex = TAB_ITEMS.findIndex((tab) => tab.key === activeTab);
    if (currentIndex < TAB_ITEMS.length - 1) {
      setActiveTab(TAB_ITEMS[currentIndex + 1].key);
    }
  };
  const handleDesktopPrimaryAction = () => {
    if (isLastTab) {
      setSummaryOpen(true);
      return;
    }
    moveToNextTab();
  };

  // Build a Shopify cart permalink that adds every line item at once —
  // structure plus each accessory — using the `/cart/{id}:{qty},...` form.
  const buildCartUrl = (
    items: Array<{ variantId: string; quantity: number }>,
  ): string => {
    const segments = items
      .map(({ variantId, quantity }) => {
        const numericId = variantId.split("/").pop();
        return numericId ? `${numericId}:${quantity}` : null;
      })
      .filter((segment): segment is string => segment !== null);
    return `https://paragon-outdoor.com/cart/${segments.join(",")}`;
  };

  // The single "Proceed to Checkout" button does the work that the prior
  // "Add to Cart" button did — build the Shopify order and redirect to the
  // cart URL. (Checkout and Add-to-Cart had identical behaviour; merged.)
  const handleCheckout = useCallback(async () => {
    if (!configId) return;
    const previewImage = view.captureImage('image/jpeg', 0.85) ?? undefined;
    const result = await functionsService.createSignaturePergolaOrder(
      configId,
      previewImage,
    );
    window.location.href = result.cartItems.length
      ? buildCartUrl(result.cartItems)
      : "https://paragon-outdoor.com/cart";
  }, [configId, view]);

  const handleDownloadDrawings = useCallback(async () => {
    if (!configId) return;
    const { downloadSignatureDrawingPdf } = await import(
      "@infrastructure/drawing/signatureDrawingPdf"
    );
    await downloadSignatureDrawingPdf({
      configuration: {
        widthValue: state.widthValue,
        depthValue: state.depthValue,
        heightValue: state.heightValue,
        hasWall: state.hasWall,
      },
      unit: state.unit,
      buildNumber: configId,
    });
  }, [
    configId,
    state.widthValue,
    state.depthValue,
    state.heightValue,
    state.hasWall,
    state.unit,
  ]);

  useEffect(() => {
    if (isMobile) return;
    setSceneVisibility(activeTab === "scene" && scenePreview);
  }, [activeTab, isMobile, scenePreview, setSceneVisibility]);

  useEffect(() => {
    stopAddMode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!summaryOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSummaryOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [summaryOpen]);

  useEffect(() => {
    if (!isShareModalOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsShareModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isShareModalOpen]);

  useEffect(() => {
    if (!isExitModalOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExitModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isExitModalOpen]);

  useEffect(() => {
    if (!isViewerReadOnly) {
      return;
    }
    stopAddMode();
    closeCustomizationModal();
    clearClickedFace();
  }, [clearClickedFace, closeCustomizationModal, isViewerReadOnly, stopAddMode]);

  useEffect(() => {
    if (!isCollaborativeMode || !service) {
      return;
    }

    const hiddenFaceIDs = service
      .getSurfacePartList()
      .filter((surfacePart) => !surfacePart.visible && surfacePart.partType !== null)
      .map((surfacePart) => surfacePart.faceId);

    hiddenFaceIDs.forEach((faceId) => {
      deletePart(faceId);
    });
  }, [
    deletePart,
    isCollaborativeMode,
    service,
    state.depthValue,
    state.hasWall,
    state.heightValue,
    state.widthValue,
  ]);

  const serializedPlacedAccessories = serializePlacedAccessories();
  const serializedPlacedAccessoriesHash = stableHash(serializedPlacedAccessories);
  serializedPlacedAccessoriesRef.current = serializedPlacedAccessories;
  const pricingResult = pricingConfigurationQuery.data
    ? calculatePrestigePricing({
        accessories: serializedPlacedAccessories.map((accessory) => ({
          faceId: accessory.faceId,
          partType: accessory.partType,
        })),
        design: state.hasWall
          ? PergolaDesign.ATTACHED
          : PergolaDesign.FREE_STANDING,
        depthValue: state.depthValue,
        heightValue: state.heightValue,
        pricingConfiguration: pricingConfigurationQuery.data,
        widthValue: state.widthValue,
        ledLight: state.ledsVisible,
        heaterQuantity: state.heaterQuantity,
      })
    : null;
  const summaryPricing: SummaryPricing = useMemo(() => {
    if (pricingConfigurationQuery.isPending) {
      return {
        helperText: "Fetching the latest pricing matrix...",
        priceLabel: "Loading...",
      };
    }

    if (pricingConfigurationQuery.error || !pricingConfigurationQuery.data) {
      return {
        helperText: "Pricing data could not be loaded.",
        priceLabel: "Unavailable",
      };
    }

    if (!pricingResult || pricingResult.totalPrice === null) {
      return {
        helperText:
          pricingResult?.issues[0]?.message ??
          "This configuration cannot be priced yet.",
        priceLabel: "Unavailable",
      };
    }

    const structureLabel = pricingResult.structurePrice
      ? formatCurrency(pricingResult.structurePrice)
      : null;
    const accessoryLabel =
      pricingResult.accessoryTotalPrice && pricingResult.accessoryTotalPrice > 0
        ? formatCurrency(pricingResult.accessoryTotalPrice)
        : null;

    // Shopify applies a 10% automatic discount at checkout. The configurator is
    // display-only here: show the full price struck through and the discounted
    // total the customer will actually be charged.
    const discountedTotal = pricingResult.totalPrice * (1 - DISCOUNT_RATE);

    return {
      helperText: accessoryLabel
        ? `${structureLabel} structure + ${accessoryLabel} accessories`
        : "Structure pricing only.",
      priceLabel: formatCurrency(discountedTotal),
      originalPriceLabel: formatCurrency(pricingResult.totalPrice),
      discountLabel: `${Math.round(DISCOUNT_RATE * 100)}% off`,
    };
  }, [
    pricingConfigurationQuery.data,
    pricingConfigurationQuery.error,
    pricingConfigurationQuery.isPending,
    pricingResult,
  ]);

  useEffect(() => {
    if (!isCollaborativeMode || !remoteConfiguration || state.isLoading) {
      return;
    }

    const remoteData: CollaborativePayload = {
      ownerID: remoteConfiguration.ownerID || collaborativeOwnerID,
      collaborative: remoteConfiguration.collaborative,
      design: remoteConfiguration.design,
      dimensions: remoteConfiguration.dimensions,
      accessories: remoteConfiguration.accessories,
      buildNumber: remoteConfiguration.buildNumber,
    };
    const remoteHash = stableHash(remoteData);
    const pendingCount = pendingLocalHashesRef.current.get(remoteHash) ?? 0;

    if (pendingCount > 0) {
      decrementPendingLocalHash(remoteHash);
      lastAppliedRemoteHashRef.current = remoteHash;
      return;
    }

    if (remoteHash === lastAppliedRemoteHashRef.current) {
      return;
    }

    let isCancelled = false;

    const applyRemoteConfiguration = async () => {
      isHydratingRemoteRef.current = true;
      try {
        const mappedState = mapPrestigeConfigurationDataToLocalState(remoteData);
        applyFrameTexture(mappedState.frameTexture);
        applyBlindTexture(mappedState.blindTexture);
        setWallMounted(mappedState.hasWall);
        await setWidth(mappedState.widthValue);
        setDepth(mappedState.depthValue);
        setHeight(mappedState.heightValue);
        await applyPlacedAccessories(mappedState.placedAccessories);

        const localLedVisibility =
          service?.getLedsVisible() ?? state.ledsVisible;
        if (mappedState.ledLight !== localLedVisibility) {
          toggleLedVisibility();
        }

        if (mappedState.heaterQuantity !== state.heaterQuantity) {
          setHeaterQuantity(mappedState.heaterQuantity);
        }

        if (!hasInitialRemoteHydrationRef.current) {
          closeCustomizationModal();
          hasInitialRemoteHydrationRef.current = true;
        }

        if (!isCancelled) {
          lastAppliedRemoteHashRef.current = remoteHash;
        }
      } finally {
        if (!isCancelled) {
          isHydratingRemoteRef.current = false;
        }
      }
    };

    void applyRemoteConfiguration();

    return () => {
      isCancelled = true;
      isHydratingRemoteRef.current = false;
    };
  }, [
    applyPlacedAccessories,
    applyBlindTexture,
    applyFrameTexture,
    closeCustomizationModal,
    collaborativeOwnerID,
    decrementPendingLocalHash,
    isCollaborativeMode,
    remoteConfiguration,
    service,
    setDepth,
    setHeight,
    setWallMounted,
    setWidth,
    state.heaterQuantity,
    state.isLoading,
    state.ledsVisible,
    setHeaterQuantity,
    toggleLedVisibility,
  ]);

  useEffect(() => {
    if (
      !isCollaborativeMode ||
      !configId ||
      !saveConfiguration ||
      !collaborativeOwnerID ||
      state.isLoading ||
      isHydratingRemoteRef.current ||
      isViewerReadOnly
    ) {
      return;
    }

    const collaborativePayload = mapLocalStateToPrestigeConfigurationData({
      localState: {
        collaborative: collaborativeEnabled,
        widthValue: state.widthValue,
        depthValue: state.depthValue,
        heightValue: state.heightValue,
        hasWall: state.hasWall,
        frameTexture,
        blindTexture,
        ledLight: state.ledsVisible,
        heaterQuantity: state.heaterQuantity,
        placedAccessories: serializedPlacedAccessoriesRef.current,
      },
      buildNumber: configId,
      ownerID: collaborativeOwnerID,
    });
    const configurationHash = stableHash(collaborativePayload);

    if (configurationHash === lastAppliedRemoteHashRef.current) {
      return;
    }

    if ((pendingLocalHashesRef.current.get(configurationHash) ?? 0) > 0) {
      return;
    }

    incrementPendingLocalHash(configurationHash);
    scheduledLocalHashRef.current = configurationHash;

    const timeoutId = window.setTimeout(() => {
      saveTimeoutRef.current = null;
      scheduledLocalHashRef.current = null;
      void saveConfiguration(collaborativePayload).catch((error) => {
        decrementPendingLocalHash(configurationHash);
        console.error("Failed to save collaborative configuration", error);
      });
    }, 350);
    saveTimeoutRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      if (saveTimeoutRef.current === timeoutId) {
        saveTimeoutRef.current = null;
      }
      if (scheduledLocalHashRef.current === configurationHash) {
        scheduledLocalHashRef.current = null;
        decrementPendingLocalHash(configurationHash);
      }
    };
  }, [
    blindTexture,
    collaborativeEnabled,
    collaborativeOwnerID,
    configId,
    decrementPendingLocalHash,
    frameTexture,
    incrementPendingLocalHash,
    isCollaborativeMode,
    isViewerReadOnly,
    saveConfiguration,
    serializedPlacedAccessoriesHash,
    state.depthValue,
    state.hasWall,
    state.heightValue,
    state.heaterQuantity,
    state.isLoading,
    state.ledsVisible,
    state.widthValue,
  ]);

  // Check if clicked face has an accessory
  const clickedFaceHasAccessory = state.clickedFaceId
    ? surfacePartList.find((p) => p.faceId === state.clickedFaceId)
      ?.partType !== null
    : false;
  const sharedTabContentProps = {
    activeTab,
    styleProps: {
      hasWall: state.hasWall,
      frameTexture,
      blindTexture,
      blindRotation: state.blindRotation,
      onWallChange: handleWallChange,
      onFrameTextureSelect: handleFrameTextureSelect,
      onBlindTextureSelect: handleBlindTextureSelect,
      onBlindRotationChange: handleBlindRotationChange,
    },
    dimensionsProps: {
      widthValue: state.widthValue,
      depthValue: state.depthValue,
      heightValue: state.heightValue,
      unit: state.unit,
      showDimensions,
      onWidthChange: handleWidthChange,
      onDepthChange: handleDepthChange,
      onHeightChange: handleHeightChange,
      onUnitChange: setUnit,
      onShowDimensionsChange: setShowDimensions,
      onDownloadDrawings: configId ? handleDownloadDrawings : undefined,
    },
    lightingProps: {
      ledsVisible: state.ledsVisible,
      onLedToggle: handleLedToggle,
      heaterQuantity: state.heaterQuantity,
      onHeaterQtyChange: handleHeaterQtyChange,
    },
    accessoriesProps: {
      widthValue: state.widthValue,
      roomCount: state.roomCount,
      surfacePartList,
      onRemovePart: handleRemovePart,
      canAddPart,
      onCustomizePart: handleCustomizePart,
      activeAccessoryType: state.activeAccessoryType,
      isAddMode: state.isAddMode,
      onSelectAccessory: handleSelectAccessory,
      onExitAddMode: handleExitAddMode,
      onResetAccessories: handleResetAccessories,
    },
    summaryProps: {
      widthValue: state.widthValue,
      depthValue: state.depthValue,
      heightValue: state.heightValue,
      roomCount: state.roomCount,
      hasWall: state.hasWall,
      unit: state.unit,
      ledsVisible: state.ledsVisible,
      frameTexture,
      louverTexture: blindTexture,
      surfacePartList,
    },
  };
  const accessoryModals = (
    <ConfiguratorAccessoryModals
      faceAccessoryModalProps={{
        faceId: state.clickedFaceId,
        widthValue: state.widthValue,
        hasAccessory: clickedFaceHasAccessory,
        onClose: clearClickedFace,
        onAddAccessory: handleModalAddAccessory,
        onRemoveAccessory: handleModalRemoveAccessory,
      }}
      accessoryCustomizationModalProps={{
        faceId: state.customizingFaceId,
        partType: state.customizingPartType,
        capabilities: state.customizingCapabilities,
        mergeDoors: getMergeDoors(),
        mergeDoorCount: getMergeDoorCount(),
        movementValue: getAccessoryMovement(),
        secondaryMovementValue: getAccessorySecondaryMovement(),
        isLeftValue: getAccessoryLeftRight(),
        textureValue: getAccessoryTexture(),
        textureSecondaryValue: getAccessoryTextureSecondary(),
        onClose: closeCustomizationModal,
        onMovementChange: handleAccessoryMovementChange,
        onSecondaryMovementChange: handleAccessorySecondaryMovementChange,
        onLeftRightChange: handleAccessoryLeftRightChange,
        onMergeDoorToggle: handleAccessoryMergeDoorToggle,
        onTextureChange: handleAccessoryTextureChange,
        onTextureSecondaryChange: handleAccessoryTextureSecondaryChange,
      }}
    />
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <div
        style={{
          // `position: fixed; inset: 0` pins the whole layout to the visible
          // viewport so the address bar collapsing on mobile browsers doesn't
          // shift the content. Combined with `flexShrink: 0` on the tab bar
          // and footer, only the middle content area scrolls.
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          // Respect iOS notch / home-indicator safe areas.
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* ── Tabs (top, fixed via flex) ── */}
        <div
          style={{
            display: "flex",
            borderBottom: "2px solid #eee",
            flexShrink: 0,
            background: "#fff",
            padding: "0 4px",
            zIndex: 2,
          }}
        >
          {TAB_ITEMS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? "#71903e" : "#888",
                  background: "transparent",
                  border: "none",
                  borderBottom: isActive
                    ? "2.5px solid #71903e"
                    : "2.5px solid transparent",
                  marginBottom: -2,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "color 0.2s",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── 3D Canvas (fixed height) ── */}
        <div
          ref={containerRef}
          style={{
            // Reduced from 50vh so the scrollable controls below have more
            // usable space without users having to fight a tiny scroll area.
            height: "clamp(220px, 38vh, 340px)",
            flexShrink: 0,
            background: "#fafafa",
            overflow: "hidden",
            position: "relative",
            borderBottom: "1px solid #eee",
          }}
        >
          {state.isLoading && (
            <LoadingOverlay progress={state.loadProgress} />
          )}
          <DimensionOverlay
            service={service}
            getCamera={view.getCamera}
            widthValue={state.widthValue}
            depthValue={state.depthValue}
            nightMode={state.nightMode}
            unit={state.unit}
            visible={!state.isLoading && showDimensions}
          />
          <div
            style={{
              position: "absolute",
              top: 12,
              left: "auto",
              right: 12,
              height: "95%",
              zIndex: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
                alignItems: "end",
                gap: 8,
                pointerEvents: "auto",
              }}>
              {isCollaborativeMode && (
                <button type="button" onClick={handleShareModalOpen} aria-label="Get sharable link" style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  height: 34,
                  padding: "0 12px",
                  border: "none",
                  borderRadius: 999,
                  background: "#ffffff",
                  boxShadow: "0 0 10px rgba(57, 59, 66, 0.2)",
                  color: "#393b42",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                >
                  <img
                    src={shareFloatingButtonIcon}
                    alt=""
                    aria-hidden="true"
                    style={{ width: 15, height: 15 }}
                  />
                  <span>Share</span>
                </button>
              )}
              <a
                href={`tel:${HELP_TEL}`}
                aria-label={`Need help? Call ${HELP_PHONE}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 34,
                  padding: "0 12px",
                  borderRadius: 999,
                  background: "#ffffff",
                  boxShadow: "0 0 10px rgba(57, 59, 66, 0.2)",
                  color: "#71903e",
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Need help?
              </a>
            </div>
          </div>
        </div>

        {/* ── Scrollable tab content (only this section scrolls) ── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            // Prevent rubber-band scrolling from chaining out to the parent
            // / browser, which on iOS can drag the whole layout.
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            padding: "16px 16px 0",
          }}
        >
          <ConfiguratorTabContent
            {...sharedTabContentProps}
            styleGap={28}
            sceneProps={{
              currentScene: state.currentScene,
              onSceneChange: setScene,
            }}
          />

          {/* Bottom spacer so content doesn't hide behind footer */}
          <div style={{ height: 16 }} />
        </div>

        {/* ── Footer (fixed at bottom via flex) ── */}
        <div
          style={{
            flexShrink: 0,
            padding: "10px 16px",
            background: "#3b3b3b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            zIndex: 2,
          }}
        >
          <div
            style={{
              color: "#ffffff",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            {summaryPricing.originalPriceLabel && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  lineHeight: 1.2,
                }}
              >
                {summaryPricing.discountLabel && (
                  <span
                    style={{
                      background: "#71903e",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "1px 6px",
                      borderRadius: 4,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {summaryPricing.discountLabel}
                  </span>
                )}
                <span
                  style={{
                    fontSize: 13,
                    opacity: 0.7,
                    textDecoration: "line-through",
                  }}
                >
                  {summaryPricing.originalPriceLabel}
                </span>
              </span>
            )}
            <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>
              {summaryPricing.priceLabel}
            </span>
            <span style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.3 }}>
              Payment plans available at checkout
            </span>
          </div>
          <button
            type="button"
            onClick={handleDesktopPrimaryAction}
            style={{
              padding: "12px 48px",
              fontSize: 15,
              fontWeight: 600,
              color: "#fff",
              background: "#71903e",
              border: "none",
              borderRadius: 24,
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            {desktopNextButtonLabel}
          </button>
        </div>

        {accessoryModals}

        <ShareModal
          open={isShareModalOpen}
          onClose={handleShareModalClose}
          onCopyLink={handleCopyShareLink}
          copyStatus={copyStatus}
          collaborativeEnabled={collaborativeEnabled}
          onCollaborativeToggle={handleCollaborativeToggle}
          canToggleCollaborative={canToggleCollaborative}
        />

        {/*
          The footer's "View Summary" button sets `summaryOpen`, so the modal
          must be mounted in this layout too — otherwise the tap does nothing
          and mobile customers have no way to reach checkout.
        */}
        <SummaryModal
          open={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          buildNumber={configId}
          metrics={desktopSummaryMetrics}
          accessories={accessorySummary}
          pricing={summaryPricing}
          pergolaDesign={state.hasWall ? "WALL MOUNTED PERGOLA" : "FREE STANDING PERGOLA"}
          louverLedKit={state.ledsVisible}
          getScene={view.getScene}
          onCheckout={handleCheckout}
          onDownloadDrawings={configId ? handleDownloadDrawings : undefined}
        />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className={`${styles.desktopShell} ${state.nightMode ? styles.nightMode : ""}`}>
      <div className={styles.contactStrip}>{CONTACT_TEXT}</div>

      <div className={styles.topOverlay}>
        <div className={styles.topLeftActions}>
          <a
            href={STORE_URL}
            className={styles.logoLink}
            aria-label="Paragon Outdoor home"
            onClick={handleLogoClick}
          >
            <img
              src={logoImg}
              alt="Paragon Outdoor logo"
              className={styles.brandLogo}
            />
          </a>
          {isCollaborativeMode && (
            <button
              type="button"
              className={styles.shareFloatingButton}
              onClick={handleShareModalOpen}
              aria-label="Get sharable link"
            >
              <img
                src={shareFloatingButtonIcon}
                alt=""
                aria-hidden="true"
                className={styles.shareFloatingButtonIcon}
              />
              <span className={styles.shareFloatingButtonText}>Get sharable link</span>
            </button>
          )}
          <button
            type="button"
            className={styles.shareFloatingButton}
            onClick={handleNightModeToggle}
            aria-label={state.nightMode ? "Disable night mode" : "Enable night mode"}
          >
            <MoonIcon />
            <span className={styles.shareFloatingButtonText}>
              {state.nightMode ? "Disable night mode" : "Enable night mode"}
            </span>
          </button>
        </div>
        {/* <h1 className={styles.pageTitle}>Build your own pergola</h1> */}
      </div>

      <div className={styles.canvasLayer}>
        <div ref={containerRef} className={styles.sceneContainer}>
          {state.isLoading && (
            <LoadingOverlay progress={state.loadProgress} />
          )}
          <DimensionOverlay
            service={service}
            getCamera={view.getCamera}
            widthValue={state.widthValue}
            depthValue={state.depthValue}
            nightMode={state.nightMode}
            unit={state.unit}
            visible={!state.isLoading && showDimensions}
          />
        </div>
      </div>

      {/* <div className={styles.leftActionRail}>
        <button
          type="button"
          onClick={actions.reset}
          className={styles.actionRoundButton}
          aria-label="Reset configuration"
        >
          <ReloadOutlined />
        </button>
      </div> */}

      <aside className={styles.floatingPanel}>
        <div className={styles.panelTabs}>
          {TAB_ITEMS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`${styles.panelTabButton} ${isActive ? styles.panelTabButtonActive : ""}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className={styles.panelContent}>
          <ConfiguratorTabContent
            {...sharedTabContentProps}
            sceneProps={{
              currentScene: state.currentScene,
              onSceneChange: handleSceneChange,
              visible: scenePreview,
              onVisibleChange: setScenePreview,
            }}
          />
        </div>

        <div className={styles.panelFooter}>
          <div className={styles.panelFooterPrice}>
            {summaryPricing.originalPriceLabel && (
              <span className={styles.panelFooterDiscountRow}>
                {summaryPricing.discountLabel && (
                  <span className={styles.discountBadge}>
                    {summaryPricing.discountLabel}
                  </span>
                )}
                <span className={styles.priceStrikethrough}>
                  {summaryPricing.originalPriceLabel}
                </span>
              </span>
            )}
            <span className={styles.panelFooterPriceValue}>
              {summaryPricing.priceLabel}
            </span>
            <span className={styles.panelFooterPriceHelper}>
              Payment plans available at checkout
            </span>
          </div>
          <button
            type="button"
            onClick={handleDesktopPrimaryAction}
            className={styles.nextButton}
          >
            {desktopNextButtonLabel}
          </button>
        </div>
      </aside>

      <a
        href={`tel:${HELP_TEL}`}
        className={styles.helpFloatingButton}
        aria-label={`Need help? Call ${HELP_PHONE}`}
      >
        <span className={styles.helpFloatingButtonTitle}>Need help?</span>
        <span className={styles.helpFloatingButtonPhone}>
          <img
            src={needHelpIcon}
            alt=""
            aria-hidden="true"
            className={styles.helpFloatingButtonPhoneIcon}
          />
          <span>{HELP_PHONE}</span>
        </span>
      </a>

      <div className={styles.bottomSummaryBar}>
        <div className={styles.summaryBlock}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Structure Color:</span>
            <span className={styles.summaryValue}>
              {desktopSummaryMetrics.structureColorName}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Louver Color:</span>
            <span className={styles.summaryValue}>
              {desktopSummaryMetrics.louvreColorName}
            </span>
          </div>
        </div>

        <div className={`${styles.summaryBlock} ${styles.summaryBlockBordered}`}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>External Dimensions:</span>
            <span className={styles.summaryValueStrong}>
              {desktopSummaryMetrics.externalDimensions}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Internal Dimensions:</span>
            <span className={styles.summaryValueStrong}>
              {desktopSummaryMetrics.internalDimensions}
            </span>
          </div>
        </div>

        <div className={`${styles.summaryBlock} ${styles.summaryBlockBordered}`}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Lighting:</span>
            <span className={styles.summaryValueStrong}>
              {state.ledsVisible
                ? "Louver LED Lighting Kit"
                : "Included LED Lighting Kit"}
            </span>
            <InfoTooltip text="Perimeter LED lighting kit included with all structures, featuring full-spectrum RGBW lighting with adjustable color and brightness. Remote control included." />
          </div>
        </div>
      </div>

      {accessoryModals}

      <ShareModal
        open={isShareModalOpen}
        onClose={handleShareModalClose}
        onCopyLink={handleCopyShareLink}
        copyStatus={copyStatus}
        collaborativeEnabled={collaborativeEnabled}
        onCollaborativeToggle={handleCollaborativeToggle}
        canToggleCollaborative={canToggleCollaborative}
      />

      <ExitConfirmModal
        open={isExitModalOpen}
        onStay={handleExitStay}
        onDiscard={handleExitDiscard}
        onCopyLink={handleCopyShareLink}
        copyStatus={copyStatus}
        collaborativeEnabled={collaborativeEnabled}
        onCollaborativeToggle={handleCollaborativeToggle}
        canToggleCollaborative={canToggleCollaborative}
      />

      <SummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        buildNumber={configId}
        metrics={desktopSummaryMetrics}
        accessories={accessorySummary}
        pricing={summaryPricing}
        pergolaDesign={state.hasWall ? "WALL MOUNTED PERGOLA" : "FREE STANDING PERGOLA"}
        louverLedKit={state.ledsVisible}
        getScene={view.getScene}
        onCheckout={handleCheckout}
        onDownloadDrawings={configId ? handleDownloadDrawings : undefined}
      />
    </div>
  );
};
